import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { LogIn, LogOut, Loader2, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store';
import { useClerkAuth } from '../../hooks/useClerkAuth';
import { useNavigate } from 'react-router-dom';

interface UserMenuProps {
  isMobile?: boolean;
  onAction?: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ isMobile = false, onAction }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout } = useClerkAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLoginClick = () => {
    navigate('/sign-in');
    onAction?.();
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      onAction?.();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (isMobile) {
    return (
      <>
        {user ? (
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoggingOut ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            {isLoggingOut ? t('navigation.loggingOut', 'Logging out...') : t('navigation.logout', 'Logout')}
          </button>
        ) : (
          <button
            onClick={handleLoginClick}
            className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <LogIn className="w-4 h-4" />
            {t('navigation.login', 'Login')}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      {user ? (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {user.email}
            </span>
          </div>
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
          >
            {isLoggingOut ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            {isLoggingOut ? t('navigation.loggingOut', 'Logging out...') : t('navigation.logout', 'Logout')}
          </button>
        </div>
      ) : (
        <button
          onClick={handleLoginClick}
          className="user-menu-login flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
        >
          <LogIn className="w-4 h-4" />
          {t('navigation.login', 'Login')}
        </button>
      )}
    </>
  );
}; 